import { useState, useMemo, useEffect, useCallback } from 'react';
import { useAuthGuard } from '@/hooks/useAuth';
import AdminLayout from '@/components/feature/AdminLayout';
import { supabase } from '@/lib/supabaseClient';
import { DURUM_RENKLERI, ARAC_DURUM_RENKLERI, TODAY } from '@/lib/constants';

interface Vehicle {
  id: number;
  ad: string;
  plaka: string;
  arac_tipi: string;
  kapasite: number;
  sofor: string;
  durum: string;
}

interface Reservation {
  id: number;
  rezervasyon_no: string;
  musteri_adi: string;
  gelis_tarihi: string;
  gelis_saati: string;
  alinis_saati: string | null;
  alinis_yeri: string;
  birak_yeri: string;
  kisi_sayisi: number;
  arac_tipi: string;
  ucret: number;
  para_birimi: string;
  atanan_arac: string | null;
  atanan_plaka: string | null;
  sofor: string | null;
  durum: string;
}

export default function AracAtamaPage() {
  useAuthGuard();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [secilenArac, setSecilenArac] = useState('');
  const [showConfirm, setShowConfirm] = useState<number | null>(null);
  const [saving, setSaving] = useState<number | null>(null);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [vRes, rRes] = await Promise.all([
      supabase.from('vehicles').select('id,ad,plaka,arac_tipi,kapasite,sofor,durum').neq('durum', 'Bakımda').order('ad'),
      supabase.from('reservations').select('id,rezervasyon_no,musteri_adi,gelis_tarihi,gelis_saati,alinis_saati,alinis_yeri,birak_yeri,kisi_sayisi,arac_tipi,ucret,para_birimi,atanan_arac,atanan_plaka,sofor,durum')
        .neq('durum', 'İptal Edildi').order('gelis_tarihi', { ascending: true }),
    ]);
    if (vRes.error) setError(vRes.error.message);
    else setVehicles(vRes.data || []);
    if (rRes.error) setError(rRes.error.message);
    else setReservations(rRes.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const atanmamis = useMemo(() =>
    reservations.filter(r => !r.atanan_arac).sort((a, b) => a.gelis_tarihi.localeCompare(b.gelis_tarihi)),
  [reservations]);

  const atananlar = useMemo(() =>
    reservations.filter(r => r.atanan_arac).sort((a, b) => a.gelis_tarihi.localeCompare(b.gelis_tarihi)),
  [reservations]);

  const handleAta = async (rezId: number) => {
    const arac = vehicles.find(v => String(v.id) === secilenArac);
    if (!arac) return;
    setSaving(rezId);
    const { error: err } = await supabase.from('reservations').update({
      atanan_arac: arac.ad,
      atanan_plaka: arac.plaka,
      sofor: arac.sofor,
      durum: 'Araç Atandı',
    }).eq('id', rezId);
    if (!err) {
      setReservations(prev => prev.map(r =>
        r.id === rezId ? { ...r, atanan_arac: arac.ad, atanan_plaka: arac.plaka, sofor: arac.sofor, durum: 'Araç Atandı' } : r
      ));
    } else {
      setError(err.message);
    }
    setSaving(null);
    setShowConfirm(null);
  };

  const handleAtamaKaldir = async (rezId: number) => {
    const { error: err } = await supabase.from('reservations').update({
      atanan_arac: null,
      atanan_plaka: null,
      sofor: null,
      durum: 'Onaylandı',
    }).eq('id', rezId);
    if (!err) {
      setReservations(prev => prev.map(r =>
        r.id === rezId ? { ...r, atanan_arac: null, atanan_plaka: null, sofor: null, durum: 'Onaylandı' } : r
      ));
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-vip-dark rounded w-1/3"></div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <div key={i} className="h-32 bg-vip-dark rounded-lg"></div>)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-display font-bold text-white">Araç Atama</h2>
          <p className="text-sm text-gray-400 mt-1">Rezervasyonlara araç ve şoför atayın</p>
        </div>
        <a href="/rezervasyonlar/yeni" className="px-4 py-2 bg-vip-gold text-vip-black text-sm font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap text-center">
          <i className="ri-add-line mr-1"></i>Yeni Rezervasyon
        </a>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3 mb-4 text-sm text-red-400">
          <i className="ri-error-warning-line mr-2"></i>{error}
        </div>
      )}

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left - Reservations */}
        <div className="w-full lg:w-3/5 space-y-6">
          {/* Unassigned */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-md bg-amber-500/15 border border-amber-500/20 flex items-center justify-center">
                <i className="ri-alert-line text-amber-400 text-xs"></i>
              </div>
              <h3 className="text-sm font-semibold text-white">Araç Atanmamış</h3>
              <span className="text-xs text-vip-gold bg-vip-gold/10 px-2 py-0.5 rounded-md border border-vip-gold/20">{atanmamis.length}</span>
            </div>
            <div className="space-y-3">
              {atanmamis.map(r => (
                <div key={r.id} className={`bg-vip-dark border rounded-lg p-4 transition-all ${showConfirm === r.id ? 'border-vip-gold/40' : 'border-vip-gold/10'}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                    <div className="flex-shrink-0">
                      <p className="text-xs text-gray-500">{r.gelis_tarihi}</p>
                      <p className="text-sm font-semibold text-vip-gold">{r.gelis_saati?.slice(0, 5)}</p>
                      {r.alinis_saati && <p className="text-[10px] text-vip-gold">Alınış: {String(r.alinis_saati).slice(0, 5)}</p>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{r.musteri_adi}</p>
                      <p className="text-xs text-gray-400">{r.alinis_yeri} → {r.birak_yeri || 'Yok'}</p>
                      <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                        <span>{r.kisi_sayisi} Kişi</span>
                        <span>{r.arac_tipi}</span>
                        <span className="text-vip-gold">{r.ucret} {r.para_birimi}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {showConfirm === r.id ? (
                        <>
                          <button onClick={() => handleAta(r.id)} disabled={saving === r.id}
                            className="px-3 py-1.5 bg-vip-gold text-vip-black text-xs font-semibold rounded-md hover:bg-vip-gold-light transition-colors whitespace-nowrap disabled:opacity-50">
                            {saving === r.id ? 'Atanıyor...' : 'Onayla'}
                          </button>
                          <button onClick={() => setShowConfirm(null)} className="px-3 py-1.5 bg-vip-black text-gray-400 text-xs rounded-md border border-vip-gold/10 hover:text-white transition-colors whitespace-nowrap">İptal</button>
                        </>
                      ) : (
                        <button onClick={() => setShowConfirm(r.id)} disabled={!secilenArac}
                          className="px-3 py-1.5 bg-vip-gold/15 text-vip-gold text-xs font-medium rounded-md border border-vip-gold/20 hover:bg-vip-gold/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap">
                          Araç Ata
                        </button>
                      )}
                      <a href={`/rezervasyonlar/yeni?edit=${r.id}`} className="w-7 h-7 rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors flex items-center justify-center">
                        <i className="ri-edit-line text-xs"></i>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
              {atanmamis.length === 0 && (
                <div className="bg-vip-dark border border-vip-gold/10 rounded-lg p-8 text-center">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-check-line text-emerald-400 text-lg"></i>
                  </div>
                  <p className="text-sm text-gray-400">Tüm rezervasyonlara araç atanmış!</p>
                </div>
              )}
            </div>
          </div>

          {/* Assigned */}
          {atananlar.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-md bg-emerald-500/15 border border-emerald-500/20 flex items-center justify-center">
                  <i className="ri-check-line text-emerald-400 text-xs"></i>
                </div>
                <h3 className="text-sm font-semibold text-white">Araç Atanmış</h3>
                <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-md border border-emerald-500/20">{atananlar.length}</span>
              </div>
              <div className="space-y-3">
                {atananlar.map(r => (
                  <div key={r.id} className="bg-vip-dark border border-vip-gold/10 rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                      <div className="flex-shrink-0">
                        <p className="text-xs text-gray-500">{r.gelis_tarihi}</p>
                        <p className="text-sm font-semibold text-vip-gold">{r.gelis_saati?.slice(0, 5)}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">{r.musteri_adi}</p>
                        <p className="text-xs text-gray-400">{r.alinis_yeri} → {r.birak_yeri || 'Yok'}</p>
                        <div className="flex flex-wrap gap-2 mt-1 text-xs text-gray-500">
                          <span className="text-vip-gold"><i className="ri-roadster-line mr-1"></i>{r.atanan_arac}</span>
                          {r.atanan_plaka && <span className="font-mono text-gray-500">{r.atanan_plaka}</span>}
                          {r.sofor && <span className="text-vip-gold"><i className="ri-steering-line mr-1"></i>{r.sofor}</span>}
                          <span>{r.ucret} {r.para_birimi}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded-md border ${DURUM_RENKLERI[r.durum] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>{r.durum}</span>
                        <button onClick={() => handleAtamaKaldir(r.id)} className="w-7 h-7 rounded-md hover:bg-amber-500/10 text-gray-500 hover:text-amber-400 transition-colors flex items-center justify-center" title="Atamayı Kaldır">
                          <i className="ri-links-line text-xs"></i>
                        </button>
                        <a href={`/rezervasyonlar/yeni?edit=${r.id}`} className="w-7 h-7 rounded-md hover:bg-vip-gold/10 text-gray-500 hover:text-vip-gold transition-colors flex items-center justify-center">
                          <i className="ri-edit-line text-xs"></i>
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right - Vehicles */}
        <div className="w-full lg:w-2/5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-md bg-vip-gold/15 border border-vip-gold/20 flex items-center justify-center">
              <i className="ri-roadster-line text-vip-gold text-xs"></i>
            </div>
            <h3 className="text-sm font-semibold text-white">Araç Seç</h3>
            {secilenArac && <span className="text-xs text-vip-gold bg-vip-gold/10 px-2 py-0.5 rounded-md border border-vip-gold/20">Seçildi</span>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
            {vehicles.map(v => {
              const isSelected = String(v.id) === secilenArac;
              const bugunTransfer = reservations.filter(r => r.atanan_arac === v.ad && r.gelis_tarihi === TODAY).length;
              return (
                <button key={v.id} onClick={() => setSecilenArac(isSelected ? '' : String(v.id))}
                  className={`text-left bg-vip-dark border rounded-lg p-4 transition-all cursor-pointer ${isSelected ? 'border-vip-gold/40 bg-vip-gold/5' : 'border-vip-gold/10 hover:border-vip-gold/20'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center ${isSelected ? 'bg-vip-gold/20' : 'bg-vip-gold/10'}`}>
                        <i className="ri-roadster-line text-vip-gold text-xs"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">{v.ad}</p>
                        <p className="text-xs text-gray-500 font-mono">{v.plaka}</p>
                      </div>
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-md ${ARAC_DURUM_RENKLERI[v.durum] || 'bg-gray-500/20 text-gray-400'}`}>{v.durum}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-400 mb-2">
                    <div className="bg-vip-black/50 rounded-md p-1.5"><p className="text-gray-500">Tip</p><p className="text-gray-300">{v.arac_tipi}</p></div>
                    <div className="bg-vip-black/50 rounded-md p-1.5"><p className="text-gray-500">Kapasite</p><p className="text-gray-300">{v.kapasite} Kişi</p></div>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400"><i className="ri-steering-line mr-1 text-vip-gold"></i>{v.sofor}</span>
                    <span className="text-gray-500">Bugün: {bugunTransfer} transfer</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}